import SizesMixin from '@/mixins/Sizes.js'
import TimeMixin from '@/mixins/Time.js'

export default
{
    name: 'app-video',

    mixins:
    [
        SizesMixin,
        TimeMixin
    ],

    props:
    {
        active: { type: Boolean, default: true }, // Is active (should load)
        maxPixelRatio: { type: Number, default: 1.5 }, // Limit de pixel ratio
        autoSize: { type: Boolean, default: true }, // Should update on window resize
        resizeThrottle: { type: Number, default: 200 }, // Throttle the resize event to prevent to much triggers
        width: { type: Number, default: 0 }, // Width (if no autoSize)
        height: { type: Number, default: 0 }, // height (if no autoSize)
        scale: { type: Number, default: 1 }, // Simple size multiplier
        sources: { type: String, default: '' }, // Sources 'http://domain.com/image-1.jpg 200x200, http://domain.com/image-2.jpg 400x400'
        downScale: { type: Boolean, default: false }, // Should load image even if it's smaller
        quality: { type: String, default: 'closest' }, // Algorithm to find the most suitable source ('strictly-below' | 'below' | 'closest' | 'above' | 'strictly-above')

        events: { type: String, default: 'play, pause' },
        loop: { type: Boolean, default: false },
        startAt: { type: Number, default: 0 },
        muted: { type: Boolean, default: false },
        controls: { type: Boolean, default: false },
        autoplay: { type: Boolean, default: false },

        fit: { type: String, default: 'cover' }
    },

    watch:
    {
        active()
        {
            if(this.active)
            {
                this.resize()
            }
        },

        width()
        {
            this.resize()
        },

        height()
        {
            this.resize()
        },

        scale()
        {
            this.resize()
        }
    },

    created()
    {
        this.time = 0

        this.size = {}
        this.size.width = this.width
        this.size.height = this.height

        // Set resolutions
        this.resolutions = {}
        this.resolutions.current = null
        this.resolutions.all = []

        const sources = this.sources.split(',')

        for(const _source of sources)
        {
            const source = _source.trim()
            const sourceParts = source.split(/\s+/)

            if(sourceParts.length === 2)
            {
                const sizeParts = sourceParts[1].split('x')

                if(sizeParts.length === 2)
                {
                    const resolution = {}
                    resolution.source = sourceParts[0]
                    resolution.width = parseInt(sizeParts[0])
                    resolution.height = parseInt(sizeParts[1])

                    this.resolutions.all.push(resolution)
                }
            }
        }

        this.throttleTimeout = null
        this.pixelRatio = Math.min(window.devicePixelRatio, this.maxPixelRatio)
    },

    mounted()
    {
        this.setVideo()
        this.setAttributes()
        this.setEvents()

        // Resize
        this.onResize()
    },

    methods:
    {
        setVideo()
        {
            this.$video = this.$refs.video
        },

        setAttributes()
        {
            if(this.loop)
            {
                this.$video.loop = true
                this.$video.setAttribute('loop', 'loop')
            }

            if(this.muted)
            {
                this.$video.muted = true
                this.$video.setAttribute('muted', 'muted')
            }

            if(this.controls)
            {
                this.$video.controls = true
                this.$video.setAttribute('controls', 'controls')
            }

            if(this.autoplay)
            {
                this.$video.autoplay = true
                this.$video.setAttribute('autoplay', 'autoplay')
            }
        },

        setEvents()
        {
            const events = this.events.split(',')

            for(let _event of events)
            {
                _event = _event.trim()

                this.$video.addEventListener(_event, () =>
                {
                    this.$emit(_event, this.$video)
                })
            }

            // Force start at
            if(this.startAt > 0)
            {
                this.$video.addEventListener('timeupdate', () =>
                {
                    if(this.$video.currentTime < this.startAt)
                    {
                        this.$video.currentTime = this.startAt
                    }
                })
            }
        },

        onTick()
        {
            if(this.$video)
            {
                const time = this.$video.currentTime

                if(time !== this.time)
                {
                    this.time = time
                    this.$emit('time-change', this.$video, this.time)
                }
            }
        },

        onResize()
        {
            // No auto size
            if(!this.autoSize)
            {
                return
            }

            // Throttle
            if(this.resizeThrottle > 0)
            {
                if(this.throttleTimeout)
                {
                    window.clearTimeout(this.throttleTimeout)
                    this.throttleTimeout = null
                }

                this.throttleTimeout = window.setTimeout(() =>
                {
                    this.resize()
                    this.throttleTimeout = null
                }, this.resizeThrottle)
            }

            // No throttle
            else
            {
                this.resize()
            }
        },

        play()
        {
            if(this.$video)
            {
                this.$video.play()
            }
        },

        pause()
        {
            if(this.$video)
            {
                this.$video.pause()
            }
        },

        toggle()
        {
            if(this.$video)
            {
                if(this.$video.paused)
                {
                    this.play()
                }
                else
                {
                    this.pause()
                }
            }
        },

        toggleMute()
        {
            if(this.$video)
            {
                if(this.$video.muted)
                {
                    this.$video.muted = false
                }
                else
                {
                    this.$video.muted = true
                }
            }
        },

        load()
        {
            if(this.$video)
            {
                this.$video.load()
            }
        },

        goTo(ratio)
        {
            if(this.$video)
            {
                this.$video.currentTime = ratio * this.$video.duration
            }
        },

        resize()
        {
            // Inactive
            if(!this.active)
            {
                return
            }

            // Pixel ratio
            this.pixelRatio = Math.min(window.devicePixelRatio, this.maxPixelRatio)

            // Auto size
            // Get element size
            if(this.autoSize)
            {
                const boudings = this.$el.getBoundingClientRect()

                this.size.width = boudings.width
                this.size.height = boudings.height
            }

            // No auto size
            // Use width and height props
            else
            {
                this.size.width = this.width
                this.size.height = this.height
            }

            // Test if width and height
            if(this.size.width > 0 && this.size.height > 0)
            {
                // Calculate effective width and height using pixel ratio and scale
                this.size.effectiveWidth = this.size.width * this.pixelRatio * this.scale
                this.size.effectiveHeight = this.size.height * this.pixelRatio * this.scale

                // Calculate score
                for(const resolution of this.resolutions.all)
                {
                    resolution.score = (resolution.width / this.size.effectiveWidth) * (resolution.height / this.size.effectiveHeight)
                }

                // Try to update resolution
                this.updateResolution()
            }
        },

        updateResolution()
        {
            let bestResolution = null

            // Test which algorithm to use
            switch(this.quality)
            {
                case 'strictly-below':
                    this.resolutions.all.sort((_a, _b) => _a.score - _b.score)

                    for(const resolution of this.resolutions.all)
                    {
                        if(resolution.score < 1 && resolution.width < this.size.effectiveWidth && resolution.height < this.size.effectiveHeight)
                        {
                            bestResolution = resolution
                        }
                    }

                    // Fallback classic 'below'
                    if(bestResolution === null)
                    {
                        for(const resolution of this.resolutions.all)
                        {
                            if(bestResolution === null || resolution.score < 1)
                            {
                                bestResolution = resolution
                            }
                        }
                    }

                    break

                case 'below':
                    this.resolutions.all.sort((_a, _b) => _a.score - _b.score)

                    for(const resolution of this.resolutions.all)
                    {
                        if(bestResolution === null || resolution.score < 1)
                        {
                            bestResolution = resolution
                        }
                    }

                    break

                case 'above':
                    this.resolutions.all.sort((_a, _b) => _b.score - _a.score)

                    for(const resolution of this.resolutions.all)
                    {
                        if(bestResolution === null || resolution.score > 1)
                        {
                            bestResolution = resolution
                        }
                    }

                    break

                case 'strictly-above':
                    this.resolutions.all.sort((_a, _b) => _b.score - _a.score)

                    for(const resolution of this.resolutions.all)
                    {
                        if(resolution.score > 1 && resolution.width > this.size.effectiveWidth && resolution.height > this.size.effectiveHeight)
                        {
                            bestResolution = resolution
                        }
                    }

                    // Fallback classic 'above'
                    if(bestResolution === null)
                    {
                        for(const resolution of this.resolutions.all)
                        {
                            if(bestResolution === null || resolution.score > 1)
                            {
                                bestResolution = resolution
                            }
                        }
                    }

                    break

                case 'closest':
                    this.resolutions.all.sort((_a, _b) => Math.abs(_a.score - 1) - Math.abs(_b.score - 1))
                    bestResolution = this.resolutions.all[0]

                    break
            }

            // Resolution changed
            if(bestResolution !== this.resolutions.current)
            {
                // No current resolutions OR should downscale OR new resolution scale is bigger
                if(this.resolutions.current === null || this.downScale || bestResolution.width * bestResolution.height > this.resolutions.current.width * this.resolutions.current.height)
                {
                    // Change source and put back time
                    this.$video.src = bestResolution.source
                    this.$video.currentTime = this.time

                    this.$emit('change')
                    this.$emit('loading-start')

                    this.resolutions.current = bestResolution
                }
            }
        }
    }
}



// WEBPACK FOOTER //
// ./src/components/app-video/script.js